import { useEffect, useState, type ReactNode, type CSSProperties } from 'react';
import { useResizeObserver } from '@/hooks/useResizeObserver';
import { cn } from '@/lib/utils';

interface RechartsCellProps {
  /** Minimum reserved height (px). Mirrors UX spec §2 chart cell sizing. */
  minHeight?: number;
  /** Optional className applied to the cell wrapper. */
  className?: string;
  /** Inline style passthrough (rare — prefer className). */
  style?: CSSProperties;
  /** The Recharts subtree (or any element that consumes the cell width/height). */
  children: ReactNode;
  /** Additional `data-testid` to append alongside the `__chart-ready` sentinel. */
  testId?: string;
}

/**
 * [T149-PR1] Mandatory wrapper for every Recharts subtree on the
 * dashboard surfaces.
 *
 * Responsibilities (per QA R3 lock from the T149 brainstorm):
 *
 * 1. Reserve a stable, non-zero `min-height` BEFORE the chart paints so
 *    layout does not jump when async data resolves.
 * 2. Use {@link useResizeObserver} to track the rendered cell box and
 *    only emit the QA sentinel `data-testid="__chart-ready"` after the
 *    first stable, non-zero paint (width × height > 0).
 * 3. Stay framework-agnostic — does not import Recharts directly so
 *    Shells that don't need a chart (KPI/List/Progress) pay no cost.
 *
 * The `__chart-ready` sentinel is the QA contract for chart-bearing E2E
 * tests: tests wait for it instead of an arbitrary timeout. Until the
 * sentinel appears, the wrapper renders a transparent placeholder of
 * `minHeight`.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
 */
export function RechartsCell({
  minHeight = 240,
  className,
  style,
  children,
  testId,
}: RechartsCellProps) {
  const { ref, size } = useResizeObserver<HTMLDivElement>();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!isReady && size.width > 0 && size.height > 0) {
      setIsReady(true);
    }
  }, [size.width, size.height, isReady]);

  return (
    <div
      ref={ref}
      className={cn('relative w-full', className)}
      style={{ minHeight, ...style }}
      data-testid={testId}
    >
      {children}
      {isReady && (
        <span
          data-testid="__chart-ready"
          aria-hidden="true"
          className="sr-only"
        />
      )}
    </div>
  );
}

export default RechartsCell;
